import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import backgroundImage from '../../images/landing-bg.png';
import rightSideImage from '../../images/hero-right-bg.png';
import mobileBg from '../../images/hero-mobile.png';
import { DoubleTickIcon } from 'icons';
import { Button } from 'components';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export const HeroSection: React.FC = () => {
  const { t, i18n } = useTranslation(); // Add i18n to access current language
  const navigate = useNavigate();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine text size for lg breakpoint based on language
  const featureTextSize =
    i18n.language === 'en' ? 'lg:text-[20px]' : 'lg:text-[18px]';

  return (
    <section
      className="relative flex flex-col md:flex-row items-center justify-between min-h-svh text-white px-smallPx md:px-mediumPx lg:px-largePx"
      style={{
        backgroundImage: isSmallScreen
          ? `url(${mobileBg})`
          : `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>

      <div className="relative z-10 flex w-full max-w-7xl mx-auto gap-10">
        <div className="w-full md:w-3/5 flex flex-col justify-center space-y-[10px] md:space-y-[20px] h-svh">
          <motion.h1
            className="text-[64px] md:text-mediumFont lg:text-largeFont xl:text-extraLargeFont font-bold leading-none text-left tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: 'easeOut' }}
          >
            <span className="font-serif font-[400]">
              {t('hero.title.part1')}
            </span>{' '}
            <span className="font-saol italic text-primary">
              {t('hero.title.part2')}
            </span>{' '}
            {isSmallScreen ? <br /> : <></>}
            <span className="font-serif font-[400]">
              {t('hero.title.part3')}
            </span>
          </motion.h1>

          <div className="flex flex-col-reverse lg:flex-row gap-[20px]">
            <div>
              <Button
                text={t('hero.button')}
                className="text-white hover:text-black"
                onClick={() => navigate('/about')}
              />
            </div>
            <p className="text-[14px] md:text-[16px] leading-relaxed text-left w-[80%] md:w-full">
              {t('hero.description')}
            </p>
          </div>
        </div>

        <div className="hidden md:flex md:w-2/5 justify-center items-center relative">
          <div
            className="absolute w-[320px] lg:w-[390px] h-[290px] lg:h-[330px]"
            style={{
              backgroundImage: `url(${rightSideImage})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <div
            className={`relative z-10 text-primary pb-6 text-[18px] ${featureTextSize} text-left`}
          >
            <div className="flex flex-row gap-2 items-center">
              <DoubleTickIcon />
              <h3>{t('hero.features.energy')}</h3>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <DoubleTickIcon />
              <h3>{t('hero.features.custom')}</h3>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <DoubleTickIcon />
              <h3>{t('hero.features.design')}</h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
